import {
  ArrowRight,
  FileText,
  FolderGit2,
  GitBranch,
  Target,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getGrowthSummary } from '../../api.js'
import { useAuth } from '../../auth-context.js'
import { useLanguage } from '../../contexts/LanguageContext.jsx'

function cleanText(value, fallback = '') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback
}

function normalizeProject(project, t) {
  return {
    id: String(project?.id || ''),
    title: cleanText(project?.title, t('projectHub.untitledProject')),
    summary: cleanText(project?.summary || project?.projectGoal, t('projectHub.projectFallbackDesc')),
    progress: Number(project?.progress || 0),
    status: project?.status || '',
  }
}

function normalizeResume(resume, t) {
  return {
    id: String(resume?.id || resume?.createdAt || ''),
    title: cleanText(resume?.fileName, t('projectHub.resumeAsset')),
  }
}

function ProjectsLoginState({ t }) {
  const entries = [
    {
      to: '/projects/my',
      icon: FolderGit2,
      title: t('projectHub.previewProjectsTitle'),
      desc: t('projectHub.previewProjectsDesc'),
    },
    {
      to: '/projects/recommendations',
      icon: Target,
      title: t('projectHub.previewImproveTitle'),
      desc: t('projectHub.previewImproveDesc'),
    },
    {
      to: '/projects/resume-assets',
      icon: FileText,
      title: t('projectHub.previewResumeTitle'),
      desc: t('projectHub.previewResumeDesc'),
    },
  ]

  return (
    <main className="page-container student-projects-page">
      <section className="student-projects-hero">
        <div>
          <span><FolderGit2 size={18} /> {t('projectHub.eyebrow')}</span>
          <h1>{t('projectHub.loginTitle')}</h1>
          <p>{t('projectHub.loginDesc')}</p>
          <div className="project-assets-login-actions">
            <Link className="primary-button" to="/login">
              {t('common.login')} <ArrowRight size={16} />
            </Link>
            <Link className="secondary-button" to="/projects/recommendations">
              {t('projectHub.previewStart')}
            </Link>
          </div>
        </div>
      </section>

      <section className="project-assets-guest-preview" aria-label={t('projectHub.previewTitle')}>
        <header>
          <span>{t('projectHub.previewTitle')}</span>
          <h2>{t('projectHub.previewHeading')}</h2>
          <p>{t('projectHub.previewDesc')}</p>
        </header>
        <div className="project-assets-actions">
          {entries.map((item) => {
            const Icon = item.icon
            return (
              <Link to={item.to} key={item.to}>
                <Icon size={16} />
                <strong>{item.title}</strong>
                <p>{item.desc}</p>
                <ArrowRight size={16} />
              </Link>
            )
          })}
        </div>
      </section>

      <section className="project-assets-github-note">
        <GitBranch size={18} />
        <div>
          <strong>{t('projectHub.githubAsEnhancer')}</strong>
          <p>{t('projectHub.githubAsEnhancerDesc')}</p>
        </div>
      </section>
    </main>
  )
}

function OutcomeOverview({ projects, projectAssets, resumeAssets, t }) {
  const active = Number(projectAssets?.active || 0)
  const completed = Number(projectAssets?.completed || 0)
  const resumes = Number(resumeAssets?.total || 0)
  return (
    <section className="project-assets-section project-assets-hero">
      <header>
        <span><FolderGit2 size={18} /> {t('projectHub.eyebrow')}</span>
        <h1>{t('projectHub.title')}</h1>
        <p>{t('projectHub.subtitle')}</p>
      </header>
      <div className="project-assets-summary" aria-label={t('projectHub.overview')}>
        <div><strong>{active}</strong><span>{t('projectHub.activeProjects')}</span></div>
        <div><strong>{completed}</strong><span>{t('projectHub.completedProjects')}</span></div>
        <div><strong>{resumes}</strong><span>{t('projectHub.resumeMaterials')}</span></div>
      </div>
      <div className="project-assets-list">
        <div className="project-assets-list-head">
          <h2>{projects.length ? t('projectHub.visibleOutcomes') : t('projectHub.noProjectsTitle')}</h2>
          <Link className="secondary-button" to="/projects/my">{t('projectHub.viewMyProjects')}</Link>
        </div>
        {projects.length ? projects.slice(0, 3).map((project) => (
          <Link to={project.id ? `/projects/${encodeURIComponent(project.id)}` : '/projects/my'} key={project.id || project.title}>
            <FolderGit2 size={16} />
            <strong>{project.title}</strong>
            <p>{project.summary}</p>
          </Link>
        )) : (
          <article>
            <FolderGit2 size={16} />
            <strong>{t('projectHub.noProjectsTitle')}</strong>
            <p>{t('projectHub.noProjectsDesc')}</p>
          </article>
        )}
      </div>
    </section>
  )
}

function ImprovementDirections({ hasProjects, t }) {
  const items = [
    {
      to: '/projects/recommendations',
      icon: Target,
      title: t('projectHub.improveProjectTitle'),
      desc: hasProjects ? t('projectHub.improveProjectDesc') : t('projectHub.startProjectDesc'),
    },
    {
      to: '/github-analysis',
      icon: GitBranch,
      title: t('projectHub.githubEnhanceTitle'),
      desc: t('projectHub.githubEnhanceDesc'),
    },
  ]
  return (
    <section className="project-assets-section">
      <div className="project-assets-section-head">
        <header>
          <span><Target size={18} /> {t('projectHub.improvement')}</span>
          <h2>{t('projectHub.improvementTitle')}</h2>
          <p>{t('projectHub.improvementDesc')}</p>
        </header>
      </div>
      <div className="project-assets-actions">
        {items.map((item) => {
          const Icon = item.icon
          return (
            <Link to={item.to} key={item.to}>
              <Icon size={16} />
              <strong>{item.title}</strong>
              <p>{item.desc}</p>
              <ArrowRight size={16} />
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function ResumeAssetsEntry({ resumes, t }) {
  return (
    <section className="project-assets-section project-resume-assets">
      <div className="project-assets-section-head">
        <header>
          <span><FileText size={18} /> {t('projectHub.resumeEntry')}</span>
          <h2>{t('projectHub.resumeTitle')}</h2>
          <p>{t('projectHub.resumeDesc')}</p>
        </header>
        <Link className="primary-button" to="/projects/resume-assets">
          {t('projectHub.prepareResume')} <ArrowRight size={16} />
        </Link>
      </div>
      <div className="project-assets-list">
        {resumes.length ? resumes.slice(0, 2).map((resume) => (
          <Link to="/projects/resume-assets" key={resume.id || resume.title}>
            <FileText size={16} />
            <strong>{resume.title}</strong>
            <p>{t('projectHub.resumeAssetDesc')}</p>
          </Link>
        )) : (
          <article>
            <FileText size={16} />
            <strong>{t('projectHub.noResumeAssetTitle')}</strong>
            <p>{t('projectHub.noResumeAssetDesc')}</p>
          </article>
        )}
      </div>
    </section>
  )
}

function ProjectsHomePage() {
  const { isLoggedIn } = useAuth()
  const { t } = useLanguage()
  const [state, setState] = useState({ loading: true, error: '', summary: null })

  useEffect(() => {
    if (!isLoggedIn) {
      setState({ loading: false, error: '', summary: null })
      return undefined
    }
    const controller = new AbortController()
    setState((current) => ({ ...current, loading: true, error: '' }))
    getGrowthSummary(controller.signal)
      .then((summary) => setState({ loading: false, error: '', summary }))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setState({ loading: false, error: error?.message || t('projectHub.loadFailed'), summary: null })
        }
      })
    return () => controller.abort()
  }, [isLoggedIn, t])

  const projects = useMemo(() => (
    Array.isArray(state.summary?.projectAssets?.latest)
      ? state.summary.projectAssets.latest.map((project) => normalizeProject(project, t))
      : []
  ), [state.summary, t])
  const resumes = useMemo(() => (
    Array.isArray(state.summary?.resumeAssets?.latest)
      ? state.summary.resumeAssets.latest.map((resume) => normalizeResume(resume, t))
      : []
  ), [state.summary, t])

  if (!isLoggedIn) return <ProjectsLoginState t={t} />

  return (
    <main className="page-container student-projects-page">
      {state.loading ? (
        <section className="student-projects-hero">
          <span><FolderGit2 size={18} /> {t('projectHub.loading')}</span>
        </section>
      ) : null}
      {state.error ? (
        <section className="student-projects-notice">
          <strong>{t('projectHub.loadFailedTitle')}</strong>
          <p>{state.error}</p>
        </section>
      ) : null}
      {state.summary ? (
        <>
          <OutcomeOverview projectAssets={state.summary.projectAssets} projects={projects} resumeAssets={state.summary.resumeAssets} t={t} />
          <ImprovementDirections hasProjects={projects.length > 0} t={t} />
          <ResumeAssetsEntry resumes={resumes} t={t} />
        </>
      ) : null}
    </main>
  )
}

export default ProjectsHomePage
